import { Text } from '@volkswagen-onehub/components-core';
import React, { useEffect } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { oneformBFFNdpsSend } from 'src/bff';
import { CodigoPostal, ConsentQuestion, DetalleMotivo, MultipleCTASelection, NombreApellidos, TelefonoEmail } from 'src/components';
import { FormController, LayoutTemplate, useIsComerciales, useOneFormContext } from 'src/feature-app';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import { ConfirmacionSAC, PantallaMotivoSAC, ThankYouSAC } from '.';
import { formDataBuilder } from '../format-fields';
import { getCommonFormData } from './FormData';

const contactoOptionsSAC = [
	{
		label: 'Email',
		value: 'Email',
	},
	{
		label: 'Teléfono',
		value: 'Telefono',
	},
	{
		label: 'Whatsapp',
		value: 'Whatsapp',
	},
];

const contactoOptionsSACComerciales = [
	{
		label: 'Email',
		value: 'Email',
	},
	{
		label: 'Teléfono',
		value: 'Telefono',
	},
	{
		label: 'Whatsapp',
		value: 'Whatsapp',
	},
];

export const SACForm = () => {
	const { initializeForm } = useOneFormContext();
	const store = useStore();
	const dispatch = useDispatch();
	const isComerciales = useIsComerciales();

	let index = 0;
	const steps: Steps[] = [
		{
			title: (
				<>
					<Text bold>¿Cómo</Text> podemos ayudarte?
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<div>
							<PantallaMotivoSAC />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'motivoSac',
			outputs: ['motivoSac'],
			outputsText: ['motivoSac'],
		},
		{
			title: (
				<>
					<Text bold>¿Podrías darnos más detalles</Text> sobre tu{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						petición?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
				</>
			),
			fields: <LayoutTemplate element={<DetalleMotivo hideCTA={true} numberOfMaxLength={1500} required={true} />} />,
			screenIndex: index++,
			name: 'MasInformacion',
			outputs: ['masInformacion'],
			outputsText: ['masInformacion'],
		},
		{
			title: (
				<>
					Para responderte, nos gustaría saber <Text bold>quién eres</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<div>
							<NombreApellidos />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'NombreApellido',
			outputs: ['name', 'surname', 'secondSurname'],
			outputsText: ['name', 'surname', 'secondSurname'],
		},
		...(isComerciales
			? [
					{
						title: (
							<>
								También nos gustaría conocer <Text bold>tu código postal</Text>
							</>
						),
						fields: <LayoutTemplate element={<CodigoPostal />} />,
						screenIndex: index++,
						name: 'CodigoPostal',
						outputs: ['codigoPostal'],
						outputsText: ['codigoPostal'],
					},
			  ]
			: []),
		{
			title: (
				<>
					<Text bold>¿En qué teléfono e email </Text>podemos localizarte?
				</>
			),
			fields: <LayoutTemplate element={<TelefonoEmail hidePrefijo={isComerciales} requiredTelefono requiredEmail />} />,
			screenIndex: index++,
			name: 'EmailTelefono',
			outputs: ['cellphone', 'email'],
			outputsText: ['cellphone', '/', 'email'],
		},
		{
			title: (
				<>
					<Text bold>¿Cómo prefieres</Text> que te{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						respondamos?
						<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
				</>
			),
			fields: <LayoutTemplate element={<MultipleCTASelection ctaValues={isComerciales ? contactoOptionsSACComerciales : contactoOptionsSAC} name="contactoSAC" nextOnClick />} />,
			screenIndex: index++,
			name: 'contactoSAC',
			outputs: ['contactoSAC'],
			outputsText: ['contactoSAC'],
			ctas: isComerciales ? contactoOptionsSACComerciales : contactoOptionsSAC,
		},
		{
			fields: <ConsentQuestion questionType="lssi" />,
			screenIndex: index++,
			name: 'lssi',
			outputs: ['lssi'],
		},
		{
			fields: <ConsentQuestion questionType="tmk" />,
			screenIndex: index++,
			name: 'tmk',
			outputs: ['tmk'],
		},
		{
			fields: <ConfirmacionSAC />,
			screenIndex: index++,
			name: 'Confirmacion',
			isLastStep: true,
			hidePreviousStep: true,
		},
	];

	const SACFormData: Partial<FormDataGroup> = {
		headers: {
			DESC_BRAND: isComerciales ? 'LCV' : 'VW',
			DESC_VN_VO: 'VN',
			DESC_GDPR_ADAPTED: 'true',
		},
		fields: { formName: 'sac', formType: 'otherform', pageCategory: 'Atención al cliente' }, // Analítica
	};

	const getFormInfo = (): {
		firstStep: Steps;
		lastStep: Steps;
		stepsHistory: number[];
		numberOfScreens: number;
		newSteps?: Steps[];
	} => {
		return {
			firstStep: steps[0],
			lastStep: steps[steps.length - 1],
			stepsHistory: [0],
			numberOfScreens: steps.length,
			newSteps: null,
		};
	};

	const sendForm = (): Promise<boolean> => {
		const { formData }: OneFormState = store.getState();
		const { fields } = formData;

		return new Promise<any>(async (resolve, reject) => {
			const data = {
				...getCommonFormData(fields),
				'fields[COMMENTS_PERSON]': fields.masInformacion,
			};
			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formData = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsSend(formData);

			if (response?.data?.status?.code == 200) {
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				resolve(false);
			}
		});
	};

	useEffect(() => {
		const { firstStep, lastStep, stepsHistory, numberOfScreens, newSteps } = getFormInfo();

		const SACFormInfo: Partial<FormInfo> = {
			formStarted: true,
			sendFormCallback: sendForm,
			fullScreen: firstStep.fullScreen ? true : false,
			lastStep,
			numberOfScreens,
			screenIndex: firstStep.screenIndex,
			step: firstStep,
			steps: newSteps ? newSteps : steps,
			stepsHistory,
			formType: 'layer',
			formTheme: 'inverted',
			notALayer: false,
			thankYouComponent: <ThankYouSAC />,
		};

		initializeForm(SACFormInfo, SACFormData);
	}, []);

	return <FormController steps={steps} screenType="layer-screen" />;
};
